import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function UserCleanupDialog() {
  const [open, setOpen] = useState(false);
  const [domains, setDomains] = useState("");
  const [emails, setEmails] = useState("");
  const [matched, setMatched] = useState(null);
  const [deleted, setDeleted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setDomains("");
    setEmails("");
    setMatched(null);
    setDeleted(null);
    setConfirming(false);
  };

  const buildBody = (dryRun) => {
    const dList = domains
      .split(/[\s,;\n]+/)
      .map((d) => d.trim().replace(/^@/, ""))
      .filter(Boolean);
    const eList = emails
      .split(/[\s,;\n]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    return { domains: dList, emails: eList, dry_run: dryRun };
  };

  const dryRun = async () => {
    setBusy(true);
    setMatched(null);
    setDeleted(null);
    try {
      const body = buildBody(true);
      if (body.domains.length === 0 && body.emails.length === 0) {
        toast.error("Adaugă cel puțin un domeniu sau email");
        return;
      }
      const { data } = await api.post("/admin/users/cleanup-by-email", body);
      setMatched(data.matched || []);
      setConfirming(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const body = buildBody(false);
      const { data } = await api.post("/admin/users/cleanup-by-email", body);
      setDeleted(data.deleted || {});
      setMatched(data.matched || []);
      setConfirming(false);
      toast.success(`Au fost șterși ${data.deleted?.users || 0} utilizatori`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20"
          data-testid="user-cleanup-trigger"
        >
          <Trash2 size={14} className="mr-1.5" />
          Curățenie utilizatori
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-white/10 max-w-2xl"
        data-testid="user-cleanup-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Trash2 className="text-red-400" size={18} />
            Cleanup utilizatori în bulk
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Șterge useri pe baza domeniilor de email sau adreselor specifice.
            <strong className="text-white"> Conturile admin nu pot fi șterse</strong>{" "}
            indiferent de filtre.
          </DialogDescription>
        </DialogHeader>

        {!deleted && (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-1.5">
                Domenii (separate prin virgulă, spațiu sau linie nouă)
              </label>
              <Textarea
                value={domains}
                onChange={(e) => setDomains(e.target.value)}
                rows={2}
                placeholder="axatc.ro, eman.team, emanteam.eu"
                className="bg-zinc-900/60 border-white/10 text-sm font-mono resize-none focus-visible:ring-red-500/40"
                data-testid="cleanup-domains"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-1.5">
                Email-uri specifice
              </label>
              <Textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={2}
                placeholder="stanescu.emilian@gmail.com"
                className="bg-zinc-900/60 border-white/10 text-sm font-mono resize-none focus-visible:ring-red-500/40"
                data-testid="cleanup-emails"
              />
            </div>

            {matched && confirming && (
              <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-3">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="text-orange-400 shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-orange-200">
                    Vor fi șterși <strong className="text-white">{matched.length}</strong> utilizatori și{" "}
                    <strong className="text-white">TOATE datele lor</strong> (simulări, recomandări, activity log,
                    quiz). Acțiunea e ireversibilă.
                  </p>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 mt-3 pl-1">
                  {matched.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">
                      Niciun utilizator nu se potrivește filtrelor.
                    </p>
                  ) : (
                    matched.map((u) => (
                      <div
                        key={u.id}
                        className="text-xs text-zinc-300 flex items-center gap-2"
                      >
                        <span className="text-red-400">•</span>
                        <span className="font-mono">{u.email}</span>
                        <span className="text-zinc-500">({u.role})</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {deleted && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="text-emerald-400" size={18} />
              <p className="text-sm text-emerald-200 font-medium">
                Cleanup complet
              </p>
            </div>
            <ul className="text-xs text-zinc-300 space-y-1 mt-2">
              <li>👤 Utilizatori șterși: <strong className="text-white">{deleted.users}</strong></li>
              <li>🎨 Simulări: <strong className="text-white">{deleted.simulations}</strong></li>
              <li>📨 Recomandări: <strong className="text-white">{deleted.recommendations}</strong></li>
              <li>📋 Activity log: <strong className="text-white">{deleted.activity_log}</strong></li>
              <li>📝 Quiz: <strong className="text-white">{deleted.quiz_responses}</strong></li>
            </ul>
          </div>
        )}

        <DialogFooter>
          {deleted ? (
            <Button
              onClick={() => {
                setOpen(false);
                reset();
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="cleanup-close"
            >
              Închide
            </Button>
          ) : confirming ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="text-zinc-400"
                data-testid="cleanup-cancel-confirm"
              >
                Înapoi
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={busy || !matched || matched.length === 0}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="cleanup-confirm-delete"
              >
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Șterge {matched?.length || 0} utilizatori
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="text-zinc-400"
                data-testid="cleanup-cancel"
              >
                Anulează
              </Button>
              <Button
                onClick={dryRun}
                disabled={busy}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="cleanup-preview"
              >
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Previzualizează (dry-run)
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
